import { useState, useEffect } from "react";
import { Loader2, LogOut, MapPin } from "lucide-react";
import { mediaUrl, getConfig } from "@/api";
import { BrandScope } from "@/theme";
import logoFull from "@/assets/brand/bonstelo-logo-full.png";
import logoFullTransparent from "@/assets/brand/bonstelo-logo-full-transparent.png";
import logoSymbol from "@/assets/brand/bonstelo-symbol.png";
import logoOnDark from "@/assets/brand/bonstelo-logo-ondark.png";

export function Loading() {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

// Environment indicator badge (owner/staff). Distinct colours for the two states.
export function EnvBadge() {
  const [env, setEnv] = useState(null);
  useEffect(() => { getConfig().then((c) => setEnv(c.environment)).catch(() => {}); }, []);
  if (!env) return null;
  const live = env === "live";
  return (
    <span data-testid="env-badge" data-env={env} title={live ? "Live-Umgebung" : "Vorschau / Test-Umgebung"}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold ring-1 whitespace-nowrap ${
        live ? "bg-emerald-100 text-emerald-700 ring-emerald-300" : "bg-amber-100 text-amber-800 ring-amber-300"
      }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500 animate-pulse-dot"}`} />
      {live ? "Live" : "Vorschau"}
    </span>
  );
}

// Bonstelo symbol (left mark of the authoritative logo) — favicon / compact spaces.
export function BonsteloIcon({ className = "h-9 w-9" }) {
  return <img src={logoSymbol} alt="Bonstelo" className={className} />;
}

// Full Bonstelo logo (symbol + wordmark), transparent background — login & larger branding.
export function BonsteloLogo({ className = "h-9" }) {
  return <img src={logoFullTransparent} alt="Bonstelo" className={className} />;
}

// Header lockup. On dark backgrounds the exact icon is kept and the wordmark is white
// (recoloured from the source image, not redrawn) for readability — no white chip.
export function BonsteloMark({ light = false }) {
  if (light) {
    return <img src={logoOnDark} alt="Bonstelo" className="h-8" />;
  }
  return <img src={logoFull} alt="Bonstelo" className="h-8" />;
}

// Wide layout with the dark Bonstelo header — used by platform screens.
export function PlatformLayout({ user, logout, children, subtitle, headerExtra }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bs-wave text-white">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <BonsteloMark light />
          <div className="flex items-center gap-3">
            {headerExtra}
            {subtitle && <span className="hidden sm:block text-white/70 text-xs font-semibold">{subtitle}</span>}
            {user && (
              <button
                data-testid="platform-logout-button"
                onClick={logout}
                className="flex items-center gap-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-xs font-bold press"
              >
                <LogOut className="h-4 w-4" /> Abmelden
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-5 py-6">{children}</main>
    </div>
  );
}

// Narrow mobile shell with dynamic per-business branding — customer & staff.
export function BusinessLayout({ business, right, children, wide = false, compact = false, shell = false }) {
  const primary = business?.primary_color;
  const secondary = business?.secondary_color;
  const logo = mediaUrl(business?.logo_url);
  return (
    <BrandScope primary={primary} secondary={secondary} className={compact || shell ? "App bs-shell-host" : "App"}>
      <div className={compact || shell ? "bs-app-shell" : "contents"}>
      <div className={`${wide ? "max-w-md lg:max-w-6xl" : "max-w-md"} mx-auto ${compact ? "bs-app-col" : shell ? "bs-app-col lg:h-full" : "min-h-screen"} bg-slate-50 sm:border-x border-slate-200 shadow-xl flex flex-col`}>
        <header data-testid="business-header" className={compact ? "bg-white border-b border-slate-200/70" : "sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200/70"}>
          <div className={`px-4 ${compact ? "py-2" : "py-3"} flex items-center gap-3`}>
            <div className={`${compact ? "h-10 w-10 rounded-xl" : "h-12 w-12 rounded-2xl"} bg-white ring-1 ring-slate-100 shadow-sm flex items-center justify-center overflow-hidden shrink-0`}>
              {logo ? (
                <img src={logo} alt={business?.name} className={compact ? "h-9 w-9 object-contain" : "h-11 w-11 object-contain"} />
              ) : (
                <span className="font-display font-extrabold text-lg" style={{ color: "var(--brand-primary)" }}>
                  {(business?.name || "B")[0]}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p data-testid="business-header-name" className={`font-display font-extrabold ${compact ? "text-[17px]" : "text-[18px]"} leading-tight tracking-tight truncate`} style={{ color: "var(--brand-primary)" }}>
                {business?.name || "Bonstelo"}
              </p>
              {business?.location ? (
                <p data-testid="business-header-address" className="text-[11px] text-slate-400 font-semibold flex items-center gap-1 min-w-0">
                  <MapPin className="h-3 w-3 shrink-0" style={{ color: "var(--brand-secondary)" }} /> <span className="truncate">{business.location}</span>
                </p>
              ) : (
                <p className="text-[11px] text-slate-400 font-semibold">Treuekarte</p>
              )}
            </div>
            {right}
          </div>
        </header>
        <main className={shell ? "flex-1 flex flex-col lg:min-h-0" : "flex-1"}>{children}</main>
      </div>
      </div>
    </BrandScope>
  );
}
